import React from 'react';

import CopyableTypography from './CopyableTypography';
import { Box, Chip, Typography } from '@mui/material';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import {
  Battery5BarOutlined,
  DeveloperBoardOutlined,
  ElectricMeterOutlined,
  SolarPowerOutlined,
  WidgetsOutlined,
} from '@mui/icons-material';

const BLOCK_TYPES = {
  "solar-meter": { label: "Solar Meter", icon: <SolarPowerOutlined fontSize="small" /> },
  "grid-meter": { label: "Grid Meter", icon: <ElectricMeterOutlined fontSize="small" /> },
  "all-in-one": { label: "Battery", icon: <Battery5BarOutlined fontSize="small" /> },
  "site-controller": { label: "Site Controller", icon: <DeveloperBoardOutlined fontSize="small" /> },
};

const COMPONENT_TYPES = {
  comp: "Computer",
  bmu: "BMU",
  bcu: "BCU",
  pcs: "PCS",
  solar: "Solar",
  meter: "Meter",
  hvac: "HVAC",
  aio_sinexcel_s90_controller: "Battery S90",
  tesla_megapack_2: "Tesla Megapack 2",
};

// A component counts as online when it reported within the last minute
const ONLINE_WITHIN_SECONDS = 60;

const STATUS_STYLES = {
  online: { label: "Online", color: "var(--c-success)", bg: "var(--c-success-bg)", dot: "#22C55E" },
  offline: { label: "Offline", color: "var(--c-danger)", bg: "var(--c-danger-bg)", dot: "#EF4444" },
};

const StatusPill = ({ status }) => {
  const style = STATUS_STYLES[status];
  return (
    <Box sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1,
      py: 0.25,
      borderRadius: "999px",
      backgroundColor: style.bg,
      color: style.color,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1.5,
    }}>
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: style.dot }} />
      {style.label}
    </Box>
  );
};

const headerCellSx = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--c-text-muted)",
  backgroundColor: "var(--c-subtle)",
  borderBottom: "1px solid var(--c-border)",
  py: 1.25,
};

const bodyCellSx = {
  fontSize: 14,
  color: "var(--c-text)",
  borderBottom: "1px solid var(--c-muted)",
  py: 1.25,
};

const BlockInfoCard = ({
  blockInfo,
}) => {

  function timeAgo(seconds) {
    if (seconds == null) {
      return "Unavailable";
    } else if (seconds < 3) {
      return "Just now";
    } else if (seconds < 60) {
      return `${seconds}s ago`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} min ago`;
    } else if (seconds < 86400) {
      const hours = Math.floor(seconds / 3600);
      return `${hours}h ago`;
    } else if (seconds < 2592000) {
      const days = Math.floor(seconds / 86400);
      return `${days}d ago`;
    } else if (seconds < 31536000) {
      const months = Math.floor(seconds / 2592000);
      return `${months}mo ago`;
    } else {
      const years = Math.floor(seconds / 31536000);
      return `${years}y ago`;
    }
  }

  // Based on the last reported activity only; the shared loading flag toggles on every poll and made the status flicker
  const componentStatus = (componentInfo) => {
    return componentInfo.elapsed_since_active != null && componentInfo.elapsed_since_active < ONLINE_WITHIN_SECONDS
      ? "online"
      : "offline";
  };

  const blockType = BLOCK_TYPES[blockInfo.block_type];
  const components = blockInfo.components || [];
  const onlineCount = components.filter(c => componentStatus(c) === "online").length;

  return (
    <Box sx={{
      backgroundColor: "var(--c-surface)",
      border: "1px solid var(--c-border)",
      borderRadius: "16px",
      overflow: "hidden",
    }}>
      {/* Header */}
      <Box sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
      }}>
        <Box sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--c-accent-bg)",
          color: "var(--c-brand)",
        }}>
          {blockType?.icon ?? <WidgetsOutlined fontSize="small" />}
        </Box>

        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--c-text)", lineHeight: 1.3 }} noWrap>
            {blockInfo.block_name}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "var(--c-text-muted)", lineHeight: 1.4 }}>
            {blockType?.label ?? blockInfo.block_type}
          </Typography>
        </Box>

        {components.length > 0 && (
          <Chip
            label={`${onlineCount} of ${components.length} online`}
            size="small"
            sx={{
              height: 22,
              fontSize: 12,
              fontWeight: 500,
              backgroundColor: onlineCount === components.length ? "var(--c-success-bg)" : "var(--c-warning-bg)",
              color: onlineCount === components.length ? "var(--c-success)" : "var(--c-warning)",
            }}
          />
        )}

        <CopyableTypography
          text={blockInfo.block_id}
        />
      </Box>

      {/* Components */}
      <TableContainer sx={{ borderTop: "1px solid var(--c-border)" }}>
        <Table sx={{ minWidth: 560 }} aria-label="Block components">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, pl: 2.5 }} align="left">Status</TableCell>
              <TableCell sx={headerCellSx} align="left">Component type</TableCell>
              <TableCell sx={headerCellSx} align="left">Last active</TableCell>
              <TableCell sx={{ ...headerCellSx, pr: 2.5 }} align="right">ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {components.map((componentInfo, index) => (
              <TableRow
                key={index}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:hover': { backgroundColor: "var(--c-subtle)" },
                }}
              >
                <TableCell sx={{ ...bodyCellSx, pl: 2.5 }} align="left">
                  <StatusPill status={componentStatus(componentInfo)} />
                </TableCell>

                <TableCell sx={{ ...bodyCellSx, fontWeight: 500 }} align="left" component="th" scope="row">
                  {COMPONENT_TYPES[componentInfo.component_type] ?? componentInfo.component_type}
                </TableCell>

                <TableCell sx={{ ...bodyCellSx, color: "var(--c-text-3)" }} align="left">
                  {timeAgo(componentInfo.elapsed_since_active)}
                </TableCell>

                <TableCell sx={{ ...bodyCellSx, pr: 2.5 }} align="right">
                  <CopyableTypography
                    text={componentInfo.component_id}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default BlockInfoCard
